import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Collapse } from "@mui/material";
import { FaCheckCircle, FaRegCircle } from "react-icons/fa";
import { OnboardingState, OnboardingStepKey, useGetOnboarding } from "hooks/useGetOnboarding";
import ChatbotTestDialog from "./ChatbotTestDialog";

type StepAction = (state: OnboardingState) => void | Promise<void>;

interface StepConfig {
  title: string;
  description: ReactNode;
  cta: string;
  // Шаг по желанию — помечается «по желанию»
  optional?: boolean;
}

const STEPS: Record<OnboardingStepKey, StepConfig> = {
  instagram: {
    title: "Подключить Instagram-аккаунт",
    description: "Войдите через Instagram — Ubee AI будет отвечать клиентам в Директ и на комментарии. Нужен профессиональный аккаунт (Бизнес или Автор).",
    cta: "Подключить",
  },
  chatbot: {
    title: "Создать чат-бота на кодовое слово",
    description: "Клиент пишет «цена» — бот сразу отвечает в Директ. Мы заполнили всё за вас, останется проверить текст и нажать «Создать».",
    cta: "Создать чат-бота",
  },
  test: {
    title: "Протестировать чат-бота",
    description: "Напишите кодовое слово в Директ вашего аккаунта с другого аккаунта Instagram и посмотрите, как ответит бот.",
    cta: "Как протестировать",
  },
  ai: {
    title: "Подключить ИИ-агента",
    description: "ИИ-агент ответит своими словами на всё, что не подошло под сценарии чат-бота. Расскажите ему о компании, проверьте на частых вопросах и включите в Instagram — займёт пару минут.",
    cta: "Настроить ИИ-агента",
    optional: true,
  },
  team: {
    title: "Пригласить сотрудника",
    description: "Менеджеры отвечают клиентам во «Входящих» там, где не справились бот и ИИ-агент.",
    cta: "Пригласить",
    optional: true,
  },
};

const dismissedKey = (orgID: string) => `onboarding:${orgID}:dismissed`;

/**
 * «Быстрый старт» на дашборде:
 * войти через Instagram → чат-бот на кодовое слово (всё заполнено) → проверить с другого аккаунта.
 * Шаги отмечаются сами. Скрывается, когда всё выполнено или пользователь его закрыл.
 */
export const OnboardingChecklist = () => {
  const orgID = localStorage.getItem("orgID");
  const navigate = useNavigate();
  const { data } = useGetOnboarding(orgID);
  const [dismissed, setDismissed] = useState(() => !!orgID && localStorage.getItem(dismissedKey(orgID)) === "1");
  const [openKey, setOpenKey] = useState<OnboardingStepKey | null>(null);
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);

  // Раскрыт первый невыполненный шаг
  useEffect(() => {
    if (data && openKey === null) {
      setOpenKey(data.steps.find((s) => !s.done)?.key ?? null);
    }
  }, [data, openKey]);

  if (!orgID || !data || dismissed) return null;
  const doneCount = data.steps.filter((s) => s.done).length;
  if (doneCount === data.steps.length) return null;

  const actions: Record<OnboardingStepKey, StepAction> = {
    // Переходная страница с кнопкой «Войти через Instagram»
    instagram: () => navigate("/admin/connect/instagram?returnTo=onboarding"),
    chatbot: (s) =>
      navigate(
        s.instagram?.connected
          ? `/admin/automations/create/keyword?onboarding=1&channelConnectionID=${s.instagram.channelConnectionID}`
          : "/admin/automations"
      ),
    test: () => setIsTestDialogOpen(true),
    // Мастер: описание компании → проверка в тестовом чате → включение в Instagram
    ai: () => navigate("/admin/ai-agents/setup"),
    team: () => navigate("/admin/members"),
  };

  // Шаги по порядку: следующий доступен, только когда выполнен предыдущий обязательный
  const isLocked = (key: OnboardingStepKey) => {
    if (key === "chatbot") return !data.steps.find((s) => s.key === "instagram")?.done;
    if (key === "test") return !data.steps.find((s) => s.key === "chatbot")?.done;
    return false;
  };

  const dismiss = () => {
    localStorage.setItem(dismissedKey(orgID), "1");
    setDismissed(true);
  };

  return (
    <div className="rounded-lg border bg-white p-6 mb-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-2xl font-bold">Быстрый старт</div>
          <div className="text-gray-500">
            Персональная настройка · {doneCount} / {data.steps.length}
          </div>
        </div>
        <Button size="small" color="inherit" onClick={dismiss}>
          Скрыть
        </Button>
      </div>

      <ul className="flex flex-col">
        {data.steps.map((step) => {
          const config = STEPS[step.key];
          const isOpen = openKey === step.key;
          const locked = isLocked(step.key);
          return (
            <li key={step.key} className="border-b last:border-b-0">
              <button
                type="button"
                className="w-full flex items-center gap-3 py-3 text-left"
                onClick={() => setOpenKey(isOpen ? null : step.key)}
              >
                {step.done ? (
                  <FaCheckCircle className="text-green-600 shrink-0" size={22} />
                ) : (
                  <FaRegCircle className="text-gray-300 shrink-0" size={22} />
                )}
                <span className={`text-lg flex-grow ${step.done ? "text-gray-400 line-through" : ""}`}>
                  {config.title}
                  {config.optional && <span className="text-sm text-gray-400 ml-2">по желанию</span>}
                </span>
              </button>
              <Collapse in={isOpen}>
                <div className="pl-9 pb-4">
                  <div className="text-gray-500 mb-3">{config.description}</div>
                  {step.key === "instagram" && data.instagram?.connected && data.instagram.username && (
                    <div className="text-sm text-green-700 mb-3">Подключён аккаунт @{data.instagram.username}</div>
                  )}
                  {locked ? (
                    <div className="text-sm text-gray-400">Сначала выполните предыдущий шаг</div>
                  ) : (
                    <Button
                      variant={step.done ? "outlined" : "contained"}
                      size="large"
                      onClick={() => actions[step.key](data)}
                      sx={{ borderRadius: "10px" }}
                    >
                      {config.cta}
                    </Button>
                  )}
                </div>
              </Collapse>
            </li>
          );
        })}
      </ul>

      <ChatbotTestDialog
        open={isTestDialogOpen}
        onClose={() => setIsTestDialogOpen(false)}
        username={data.instagram?.username}
        keywords={data.chatbot?.keywords}
        direct={data.chatbot?.direct ?? true}
        comment={data.chatbot?.comment}
      />
    </div>
  );
};

export default OnboardingChecklist;
